/**
 * Portalless modal — position fixed overlay + centred card.
 * Escape + backdrop click close. Stays simple: no @radix/portal, no focus trap.
 */
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Modal({ open, onClose, title, subtitle, children, size = 'md' }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    document.addEventListener('keydown', onKey)
    // Lock background scroll while open.
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    // Tamaño extra para modales con tablas anchas (Nueva devolución,
    // Confirmar review). Se acerca al ancho de la pantalla en desktop.
    '2xl': 'max-w-6xl',
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/40 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className={cn(
          'w-full bg-white rounded-[18px] flex flex-col max-h-[90vh]',
          widths[size]
        )}
        style={{ boxShadow: '0 20px 50px rgba(20,80,42,0.18)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header unificado (handoff #26): título + subtítulo a la izquierda,
            botón de cerrar (X) a la derecha. */}
        <div className="flex items-start justify-between gap-4 sm:gap-6 border-b border-expo-line px-5 sm:px-[26px] pt-5 sm:pt-[22px] pb-4 sm:pb-[18px]">
          <div className="min-w-0">
            <h3
              className="font-display text-[20px] sm:text-[23px]"
              style={{ fontWeight: 800, color: '#143A22', letterSpacing: '-0.01em', lineHeight: 1.12 }}
            >
              {title}
            </h3>
            {subtitle ? (
              <div style={{ fontSize: 14, color: '#8B9B8F', fontWeight: 500, marginTop: 4 }}>
                {subtitle}
              </div>
            ) : null}
          </div>
          <button
            onClick={onClose}
            title="Cerrar"
            className="flex-none flex items-center justify-center transition-colors"
            style={{ width: 40, height: 40, borderRadius: 12, border: '1.5px solid #E6EFE3', background: '#fff', color: '#5C6B60' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#F6FAEF')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 sm:p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}

/**
 * Form-group helpers so pages don't repeat label/input styling.
 */
export function Field({ label, children, hint, required }) {
  return (
    <label className="block">
      <span
        className="block mb-1.5"
        style={{ fontSize: '13px', fontWeight: 700, color: '#15321F' }}
      >
        {label}
        {required ? <span className="ml-0.5" style={{ color: '#C0392B' }}>*</span> : null}
      </span>
      {children}
      {hint ? <span className="block text-xs mt-1" style={{ color: '#8B9B8F' }}>{hint}</span> : null}
    </label>
  )
}

// Estilo compartido de input/select del handoff (1.5px borde, radio 13, peso 600).
const FIELD_STYLE = {
  border: '1.5px solid #E6EFE3',
  borderRadius: '13px',
  padding: '14px 16px',
  fontWeight: 600,
  color: '#15321F',
}

export function Input({ className, style, ...props }) {
  return (
    <input
      className={cn(
        'w-full outline-none transition-colors focus:border-[#1E6E3A]',
        className
      )}
      style={{ ...FIELD_STYLE, ...style }}
      {...props}
    />
  )
}

export function Select({ className, children, style, ...props }) {
  return (
    <select
      className={cn(
        'w-full outline-none transition-colors bg-white focus:border-[#1E6E3A]',
        className
      )}
      style={{ ...FIELD_STYLE, ...style }}
      {...props}
    >
      {children}
    </select>
  )
}
